/**
 * Identity rules from the PRD, named once. They are product rules, not per-environment settings: session lengths
 * are configuration (src/config/env.ts) because organisations set them (FR-ADMIN-047).
 */

const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

export const IDENTITY_POLICY = {
  /** Terms of service version people accept at first sign-in; a new version asks again (FR-ADMIN-001). */
  termsVersion: "2026-10-01",
  /** Minimum age to hold an account (FR-ADMIN-001). */
  minimumAge: 18,
  /** One-time codes: 6 digits, single use, valid 10 min, locked 15 min after 5 wrong tries (27 §5 conventions). */
  code: {
    digits: 6,
    validMs: 10 * MS_PER_MINUTE,
    maxAttempts: 5,
    lockMs: 15 * MS_PER_MINUTE,
    /** At most 10 codes per email per hour (FR-ADMIN-003). */
    perWindow: 10,
    windowMs: 60 * MS_PER_MINUTE,
    /** Wait before "Send a new code" is offered again (product default). */
    resendAfterMs: 30 * MS_PER_SECOND,
  },
  /** Random bytes in every token: 16 bytes = 128 bits (FR-ADMIN-032, FR-COLLAB-101). */
  tokenBytes: 16,
  /** Invitations last 14 days (FR-ADMIN-026). */
  invitationMs: 14 * MS_PER_DAY,
  /** Project access requests expire after 14 days (FR-ADMIN-028). */
  projectRequestMs: 14 * MS_PER_DAY,
  /** A hosted sign-in started here must finish within this time; the callback code is single use and short-lived. */
  signInRequestMs: 10 * MS_PER_MINUTE,
  callbackCodeMs: MS_PER_MINUTE,
  /** Share links: passwords of 8+ characters; 5 failures lock the link for 15 min for that visitor (FR-COLLAB-103). */
  share: { passwordMinLength: 8, maxAttempts: 5, lockMs: 15 * MS_PER_MINUTE },
  /** Content stays in the organisation's region, India (Mumbai) by default and fixed at creation (FR-ADMIN-075). */
  storageRegion: { id: "in-mumbai", label: "India (Mumbai)" },
} as const;

export type IdentityPolicy = typeof IDENTITY_POLICY;

export const TIME = { MS_PER_SECOND, MS_PER_MINUTE, MS_PER_DAY } as const;
